import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import Loading from '../../../components/Loading'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'

export default function PreferredCommunicationsModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const dropdownOptions = useSelector(selectDropdownOptions)
  const { handleSubmit, control, formState: { errors }, reset } = useForm();
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const customerId = modal.options?.customerId
  const [customerInfo, setCustomerInfo] = useState(null)
  const [dncSourceCodeKey, setDncSourceCodeKey] = useState(null);
  const [loadedCustomerId, setLoadedCustomerId] = useState(null)
  const active = modal.is_active && modal.type === 'preferred_communications_modal'
  const inFlightCustomerId = useRef(null)

  useEffect(() => {
    return () => {
      dispatch(setModal({ is_active: false }));
      setCustomerInfo(null);
    };
  }, []);

  // This modal stays mounted in the Footer and its checkboxes take the loaded customer only as
  // defaultValue (applied once at registration). Clear the previous customer on every open and drop
  // late responses, so a previous customer's consent flags can never become this customer's.
  useEffect(() => {
    if (active && customerId) {
      resetState()
      getCustomerInformation(customerId)
    } else {
      inFlightCustomerId.current = null
    }
  }, [active, customerId])

  const getCustomerInformation = async (customerId) => {
    inFlightCustomerId.current = customerId
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}`)
    try {
      const response = await request.get()
      if (inFlightCustomerId.current !== customerId) return
      if (response.status === 200 || response.status === 201) {
        setCustomerInfo(response?.data?.data)
        setLoadedCustomerId(customerId)
        if (response?.data?.data?.dnc_source_dropdown_option_id) {
          setDncSourceCodeKey(dropdownOptions?.find((ddo) => ddo?.id === response.data.data.dnc_source_dropdown_option_id)?.code_key)
        } else {
          setDncSourceCodeKey(null)
        }
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      if (inFlightCustomerId.current !== customerId) return
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const resetState = () => {
    setCustomerInfo(null);
    setLoadedCustomerId(null);
    setDncSourceCodeKey(null);
    setSaving(false);
    reset()
  };

  const handleCancel = () => {
    resetState();
    dispatch(setModal({ is_active: false }));
  };

  const isLoaded = customerInfo && loadedCustomerId === customerId

  const onSubmit = async (formData) => {
    setSaving(true)
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/preferred_communications`)
    try {
      const response = await request.modify(formData)
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        handleCancel()
        dispatch(setNotification({ type: 'success', body: '' }))
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  };

  return active ? (
    <form onSubmit={handleSubmit(onSubmit)} id="preferred_communications_modal_form" data-testid="preferred-communications-modal">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card is-flex is-flex-direction-column column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized" data-testid="preferred-communications-modal-title">
              Preferred Communications
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: "110px", overflow: 'hidden' }}
          >
            {!isLoaded && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  <span className="has-text-center"><Loading /></span>
                </div>
              </div>
            )}
            {isLoaded && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  <h3>When customer states to do not contact them anymore, determine which touch points.</h3>
                  <h3>By unchecking the applicable boxes below, we will no longer contact the customer via the checked methods.</h3>
                </div>
                <div className="column">
                  <h3><strong>Email</strong></h3>
                  <label>
                    <Controller
                      name="is_consent_email_marketing"
                      control={control}
                      defaultValue={customerInfo.is_consent_email_marketing || false}
                      render={({ field }) => (
                        <input {...field} type="checkbox" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      )}
                    /> Email Marketing
                  </label>
                </div>
                <div className="column">
                  <h3><strong>SMS</strong></h3>
                  <label>
                    <Controller
                      name="is_consent_sms_transactional"
                      control={control}
                      defaultValue={customerInfo.is_consent_sms_transactional || false}
                      render={({ field }) => (
                        <input {...field} type="checkbox" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      )}
                    /> Transactional SMS
                  </label><br />
                  <label>
                    <Controller
                      name="is_consent_sms_marketing"
                      control={control}
                      defaultValue={customerInfo.is_consent_sms_marketing || false}
                      render={({ field }) => (
                        <input {...field} type="checkbox" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      )}
                    /> SMS Marketing
                  </label>
                </div>
                <div className="column">
                  <h3><strong>Phone</strong></h3>
                  <u>Global</u><br />
                  <label>
                    <Controller
                      name="is_do_not_call"
                      control={control}
                      defaultValue={customerInfo.is_do_not_call || false}
                      disabled={['dnc_source_bankruptcy', 'dnc_source_dmc'].includes(dncSourceCodeKey) && customerInfo?.has_active_loan}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    /> Do Not Call (NOTE: We will not call any phone number for any reason)
                    {['dnc_source_bankruptcy', 'dnc_source_dmc'].includes(dncSourceCodeKey) && customerInfo?.has_active_loan && (
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip={'NOTE: If customer has an active Bankruptcy or Debt Management Company for an active loan, this will be set due to those.'}
                      >
                        <FontAwesomeIcon
                          icon={faQuestionCircle}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </label><br /><br />
                  <u>Specific Type</u><br />
                  <label>
                    <Controller
                      name="is_consent_phone_marketing"
                      control={control}
                      defaultValue={customerInfo.is_consent_phone_marketing || false}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    /> Phone Marketing
                  </label><br />
                  <label>
                    <Controller
                      name="is_home_phone_do_not_call"
                      control={control}
                      defaultValue={customerInfo.is_home_phone_do_not_call || false}
                      render={({ field }) => (
                        <input {...field} type="checkbox" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      )}
                    /> Home Phone Do Not Call
                  </label><br />
                  <label>
                    <Controller
                      name="is_cell_phone_do_not_call"
                      control={control}
                      defaultValue={customerInfo.is_cell_phone_do_not_call || false}
                      render={({ field }) => (
                        <input {...field} type="checkbox" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      )}
                    /> Cell Phone Do Not Call
                  </label><br />
                  <label>
                    <Controller
                      name="is_work_phone_do_not_call"
                      control={control}
                      defaultValue={customerInfo.is_work_phone_do_not_call || false}
                      render={({ field }) => (
                        <input {...field} type="checkbox" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      )}
                    /> Work Phone Do Not Call
                  </label>
                </div>
              </div>
            )}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button data-testid="preferred-communications-modal-cancel" type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>
              <button data-testid="preferred-communications-modal-save" type="submit" className="button is-link is-small" disabled={saving && 'disabled'}>{saving ? 'Saving' : 'Save'}</button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
